import { fmtDate } from "@/lib/utils";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Lock, CreditCard, ShieldCheck, MapPin, ArrowRight, Wallet } from "lucide-react";

export default function Checkout() {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [config, setConfig] = useState(null);
  const [mode, setMode] = useState("full");
  const [busy, setBusy] = useState(false);
  const [coupon, setCoupon] = useState("");

  const applyCoupon = async () => {
    try {
      const { data } = await api.post(`/bookings/${bookingId}/apply-coupon`, { code: coupon });
      toast.success(`Gutschein eingelöst: −${data.discount} €`);
      const b = await api.get(`/bookings/${bookingId}`); setBooking(b.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  useEffect(() => {
    (async () => {
      const [b, c] = await Promise.all([
        api.get(`/bookings/${bookingId}`),
        api.get("/payments/config"),
      ]);
      setBooking(b.data); setConfig(c.data);
    })();
  }, [bookingId]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { booking_id: bookingId, origin_url: window.location.origin, mode });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail)); setBusy(false);
    }
  };

  const payPayPal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/paypal/create", { booking_id: bookingId, origin_url: window.location.origin, mode });
      if (!data.approve_url) throw new Error("no url");
      window.location.href = data.approve_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail)); setBusy(false);
    }
  };

  if (!booking) return <div className="eb-container py-16 text-slate-400">Lädt…</div>;
  const amount = mode === "deposit" ? Math.round(booking.customer_price * 0.3 * 100) / 100 : booking.customer_price;

  return (
    <div className="eb-container py-12 max-w-3xl">
      <h1 className="font-head text-3xl font-bold mb-2">Sicherer Checkout</h1>
      <p className="text-slate-400 mb-8 flex items-center gap-2"><Lock className="w-4 h-4 text-emerald-400" /> Verschlüsselte Zahlung über Stripe{config?.paypal_ready ? " oder PayPal" : ""}</p>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-5">
          <div className="eb-card p-6">
            <h3 className="font-head font-semibold mb-4">Zahlungsart</h3>
            <div className="space-y-3">
              {[["full","Vollständige Zahlung",`${booking.customer_price} €`],["deposit",`Anzahlung (${config?.deposit_percent || 30}%)`,`${Math.round(booking.customer_price*0.3*100)/100} €`]].map(([v,l,a])=>(
                <label key={v} className={`flex items-center justify-between rounded-lg border p-4 cursor-pointer transition-colors ${mode===v?"border-[#FF5A1F] bg-[#FF5A1F]/5":"border-slate-800"}`} data-testid={`pay-mode-${v}`}>
                  <div className="flex items-center gap-3">
                    <input type="radio" checked={mode===v} onChange={()=>setMode(v)} className="accent-[#FF5A1F]" />
                    <span className="text-slate-200">{l}</span>
                  </div>
                  <span className="mono text-slate-300">{a}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="eb-card p-6">
            <h3 className="font-head font-semibold mb-4">Zahlungsmethode</h3>
            {booking.status === "reserviert" && <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 text-sm text-amber-200 mb-3" data-testid="checkout-locked-pending">Der Transportpartner prüft deine Anfrage noch. Die Zahlung wird freigeschaltet, sobald er bestätigt hat – du erhältst eine E-Mail.</div>}
            {["abgelehnt", "abgelaufen", "storniert"].includes(booking.status) && <div className="rounded-lg border border-red-500/40 bg-red-500/5 p-4 text-sm text-red-200 mb-3" data-testid="checkout-locked-closed">Diese Buchung ist {booking.status === "abgelehnt" ? "vom Partner abgelehnt" : booking.status === "abgelaufen" ? "abgelaufen" : "storniert"} – eine Zahlung ist nicht möglich. <Link to={`/angebote/${booking.request_id}`} className="underline">Andere Route wählen</Link></div>}
            {booking.status === "freigegeben" && booking.payment_status !== "paid" && (<>
            <button onClick={pay} disabled={busy} className="eb-btn-primary w-full mb-3" data-testid="checkout-stripe-btn">
              <CreditCard className="w-4 h-4" /> {busy ? "Weiterleitung…" : `Mit Karte / SEPA bezahlen · ${amount} €`}
            </button>
            {config?.paypal_ready ? (
              <button onClick={payPayPal} disabled={busy} data-testid="checkout-paypal-btn"
                className="w-full rounded-lg bg-[#FFC439] hover:bg-[#f5b921] text-[#003087] font-bold py-3 px-4 flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                <Wallet className="w-4 h-4" /> Mit PayPal bezahlen · {amount} €
              </button>
            ) : (
              <div className="rounded-lg border border-slate-800 bg-[#0B0F17] p-3 text-sm text-slate-500 flex items-center gap-2" data-testid="checkout-paypal-pending">
                PayPal – vorbereitet (Aktivierung durch Betreiber)
              </div>
            )}
            <p className="text-xs text-slate-500 mt-3">Testkarte: 4242 4242 4242 4242, beliebiges zukünftiges Datum & CVC.</p>
            </>)}
            {booking.payment_status === "paid" && <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-4 text-sm text-emerald-200" data-testid="checkout-paid">Bezahlt – deine Buchung ist verbindlich.</div>}
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="eb-card p-6 lg:sticky lg:top-24">
            <h3 className="font-head font-semibold mb-4">Deine Buchung</h3>
            <div className="flex items-center gap-2 font-head font-semibold mb-3">
              <MapPin className="w-4 h-4 text-[#FF5A1F]" /> {booking.route_label?.split(" → ")[0]}
              <ArrowRight className="w-4 h-4 text-slate-600" /> {booking.route_label?.split(" → ")[1]}
            </div>
            <div className="text-sm text-slate-400 space-y-1.5">
              <div className="flex justify-between"><span>Abfahrt</span><span>{fmtDate(booking.abfahrt)}</span></div>
              <div className="flex justify-between"><span>Partner</span><span>{booking.partner_name}</span></div>
              <div className="flex justify-between"><span>Partnerpreis</span><span className="mono">{booking.partner_price} €</span></div>
              <div className="flex justify-between"><span>Vermittlungsgebühr</span><span className="mono">{booking.commission} €</span></div>
            </div>
            <div className="border-t border-slate-800 mt-4 pt-4 flex justify-between items-center">
              <span className="text-slate-300">Gesamt</span>
              <span className="font-head text-2xl font-bold text-[#FF5A1F]">{booking.customer_price} €</span>
            </div>
            {booking.coupon_code ? (
              <div className="text-xs text-emerald-400 mt-2" data-testid="coupon-applied">Gutschein {booking.coupon_code}: −{booking.discount} € (statt {booking.original_price} €)</div>
            ) : booking.payment_status !== "paid" && (
              <div className="flex gap-2 mt-4">
                <input className="eb-input !py-2 text-sm uppercase" placeholder="Gutscheincode" value={coupon} onChange={(e) => setCoupon(e.target.value)} data-testid="coupon-input" />
                <button onClick={applyCoupon} disabled={!coupon.trim() || busy} className="eb-btn-outline !py-2 text-sm" data-testid="coupon-apply-btn">Einlösen</button>
              </div>
            )}
            <div className="flex items-center gap-2 text-xs text-slate-500 mt-4"><ShieldCheck className="w-4 h-4 text-emerald-400" /> Versicherter Transport über geprüften Partner</div>
          </div>
        </div>
      </div>
    </div>
  );
}
